import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Send } from 'lucide-react';

// X (Twitter) Icon Component
function XIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Telegram Icon Component  
function TelegramIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}

function LogoMark() {
  return (
    <motion.div
      className="relative flex items-center gap-2.5"
      whileHover="hover"
      initial="idle"
    >
      {/* Glow backdrop behind mark */}
      <div className="relative">
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0,212,255,0.25) 0%, transparent 70%)',
            filter: 'blur(8px)',
            width: '44px',
            height: '44px',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          variants={{
            idle: { opacity: 0.5, scale: 1 },
            hover: { opacity: 0.9, scale: 1.2 },
          }}
          animate={{ opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.img
          src="/assets/rax-logo.png"
          alt="RAX"
          className="relative z-10 h-10 w-auto drop-shadow-[0_0_6px_rgba(0,212,255,0.3)]"
          variants={{
            idle: { scale: 1 },
            hover: { scale: 1.06 },
          }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        />
      </div>
    </motion.div>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <div className="mx-auto max-w-7xl px-6 py-4">
        <div className="flex items-center justify-between rounded-2xl border border-rax-border/50 bg-rax-bg/80 px-6 py-3 backdrop-blur-xl">
          <a href="/" className="flex items-center" data-testid="navbar-logo">
            <LogoMark />
          </a>

          <div className="hidden items-center gap-5 md:flex">
            <a
              href="https://docs.raxprotocol.xyz/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm text-rax-body transition-colors hover:text-rax-cyan"
              data-testid="nav-docs-link"
            >
              Docs
            </a>
            <a
              href="https://x.com/raxprotocol"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-rax-body transition-colors hover:text-rax-cyan hover:bg-rax-card/30"
              data-testid="nav-twitter-link"
              aria-label="X (Twitter)"
            >
              <XIcon size={18} />
            </a>
            <a
              href="https://t.me/raxprotocol"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-rax-body transition-colors hover:text-rax-cyan hover:bg-rax-card/30"
              data-testid="nav-telegram-link"
              aria-label="Telegram"
            >
              <TelegramIcon size={18} />
            </a>
            <a
              href="https://discord.gg/pcHDVySGnc"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-rax-body transition-colors hover:text-rax-cyan hover:bg-rax-card/30"
              data-testid="nav-discord-link"
              aria-label="Discord"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286z"/></svg>
            </a>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSf1KHoi2JeuJ-BH4y426b-8Or6mxC1GFB_Og_9lgABGXBn6_g/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg bg-rax-cyan px-5 py-2 font-heading text-sm font-semibold text-rax-bg transition-all hover:bg-rax-cyan/90 hover:shadow-[0_0_20px_rgba(0,212,255,0.3)]"
              data-testid="nav-join-whitelist-btn"
            >
              Join Whitelist
            </a>
            <a
              href="https://app.raxprotocol.xyz/"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-rax-cyan/30 bg-rax-cyan/5 px-5 py-2 font-heading text-sm font-semibold text-rax-cyan transition-all hover:bg-rax-cyan/10 hover:border-rax-cyan/50 hover:shadow-[0_0_16px_rgba(0,212,255,0.15)]"
              data-testid="nav-launch-app-btn"
            >
              Launch App
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="flex items-center justify-center h-11 w-11 rounded-lg text-rax-heading md:hidden active:bg-rax-card/50 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-2 rounded-2xl border border-rax-border/50 bg-rax-bg/95 p-6 backdrop-blur-xl md:hidden touch-scroll"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-1">
                <a 
                  href="https://docs.raxprotocol.xyz/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center h-12 px-3 rounded-lg text-sm text-rax-body hover:text-rax-cyan active:bg-rax-card/50 transition-colors" 
                  onClick={() => setMobileOpen(false)}
                >
                  Docs
                </a>
                <a 
                  href="https://x.com/raxprotocol" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center h-12 px-3 rounded-lg text-sm text-rax-body hover:text-rax-cyan active:bg-rax-card/50 transition-colors" 
                  onClick={() => setMobileOpen(false)}
                >
                  Twitter
                </a>
                <a 
                  href="https://t.me/raxprotocol" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center h-12 px-3 rounded-lg text-sm text-rax-body hover:text-rax-cyan active:bg-rax-card/50 transition-colors" 
                  onClick={() => setMobileOpen(false)}
                >
                  Telegram
                </a>
                <a 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSf1KHoi2JeuJ-BH4y426b-8Or6mxC1GFB_Og_9lgABGXBn6_g/viewform" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="mt-3 flex items-center justify-center h-12 rounded-lg bg-rax-cyan px-5 font-heading text-sm font-semibold text-rax-bg active:bg-rax-cyan/80 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Join Whitelist
                </a>
                <a 
                  href="https://app.raxprotocol.xyz/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="mt-2 flex items-center justify-center h-12 rounded-lg border border-rax-cyan/30 bg-rax-cyan/5 px-5 font-heading text-sm font-semibold text-rax-cyan active:bg-rax-cyan/10 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Launch App
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}
